import React from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Step, StepStatus } from '../../../app/entities/step';
import { DropdownMenu } from '../../../components/modals/dropdown-menu/DropdownMenu';
import { context } from '../../../app/context';
import { TaskPropertyButton } from '../../tasks/components/task-property.button';

interface StepStatusSelectorProps {
  stepId: string;
}

export const StepStatusSelector: React.FC<StepStatusSelectorProps> = observer(({ stepId }) => {
  const step = Step.getOne(stepId)!;
  const currentMember = context.member;

  const handleChangeStatus = async (newStatus: StepStatus) => {
    try {
      await step.update({ status: newStatus });
    } catch (e) {
      console.error(e);
    }
  };

  return currentMember?.role !== 'guest' ? (
    <DropdownMenu
      items={Object.entries(Step.StatusMapper).map(([key, { title }]) => ({
        title,
        onSelect: () => handleChangeStatus(key as StepStatus),
        icon: <Dot data-status={key as StepStatus} />,
      }))}
      trigger={
        <div>
          <TaskPropertyButton
            icon={<Dot data-status={step.status} />}
            data-status={step.status}
            label={Step.StatusMapper[step.status].title}
            isActive={true}
          />
        </div>
      }
    />
  ) : (
    <TaskPropertyButton
      icon={<Dot data-status={step.status} />}
      data-status={step.status}
      label={Step.StatusMapper[step.status].title}
      isDisabled
    />
  );
});

export const Dot = styled.div`
  position: relative;
  height: 1.6rem;
  width: 1.6rem;
  border-radius: 50%;
  border: 2px solid;
  border-color: var(--color-grayscale-white, #fafafa);

  &:after {
    content: '';
    border-radius: 50%;
    position: absolute;
    top: 50%;
    left: 50%;
    height: 1rem;
    width: 1rem;
    transform: translate(-50%, -50%);
  }

  &[data-status='on-hold'] {
    border-color: var(--color-grayscale-light-slate, #7a8296);

    &:after {
      height: 0.4rem;
      width: 0.4rem;
      background: var(--color-grayscale-light-slate, #7a8296);
    }
  }

  &[data-status='in-progress'] {
    border-color: var(--color-primary-crayola);

    &:after {
      background: conic-gradient(var(--color-primary-crayola) 90deg, transparent 90deg);
    }
  }

  &[data-status='needs-review'] {
    border-color: var(--color-secondary-saffron, #f8a72d);

    &:after {
      background: conic-gradient(var(--color-secondary-saffron) 180deg, transparent 180deg);
    }
  }

  &[data-status='changes-requested'] {
    border-color: var(--color-secondary-roman, #db5c66);

    &:after {
      background: conic-gradient(var(--color-secondary-roman) 270deg, transparent 270deg);
    }
  }

  &[data-status='canceled'] {
    border-color: var(--color-grayscale-light-slate, #7a8296);

    &:after,
    &:before {
      content: '';
      position: absolute;
      background-color: var(--color-grayscale-light-slate);
      width: 0.2rem;
      height: 0.8rem;
      top: 50%;
      left: 50%;
      transform-origin: center center;
    }

    &:after {
      transform: translate(-50%, -50%) rotate(-45deg);
    }

    &:before {
      transform: translate(-50%, -50%) rotate(45deg);
    }
  }

  &[data-status='approved'] {
    border-color: var(--color-primary-puerto-rico);

    &:after {
      background: var(--color-primary-puerto-rico);
    }
  }

  &[data-status='done'] {
    border-color: var(--color-primary-puerto-rico);

    &:after {
      background: var(--color-primary-puerto-rico);
    }
  }
`;
